import { X, Filter } from "lucide-react";

// Modal used for both creating and editing a role. Shares the parent's
// global formData (so the "Use Filter" integration keeps working through
// the shared Filters modal) and delegates submit/cancel back up.
export default function RoleFormPopup({
  showRoleForm,
  editingRole,
  formData,
  handleInputChange,
  setFormData,
  wizardRoleType,
  setWizardRoleType,
  roleAssignmentType,
  setRoleAssignmentType,
  filterResults,
  setShowFilters,
  onSubmit,
  onCancel,
}) {
  if (!showRoleForm) return null;

  const handleAssignAll = (e) => {
    setRoleAssignmentType(e.target.value);
    setFormData({ ...formData, assignmentType: "all" });
  };

  const handleAssignFilter = (e) => {
    setRoleAssignmentType(e.target.value);
    setFormData({ ...formData, assignmentType: "filter" });
  };

  const isEditingSystemRole = editingRole?.type === "system";

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white shadow-xl relative w-[700px] h-[500px] flex flex-col border border-gray-300">
        <div className="flex items-center justify-between p-4 border-b border-gray-300 flex-shrink-0">
          <h3 className="text-xl font-bold">
            {editingRole ? "Edit Role" : "Add Role"}
          </h3>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4">
          {/* noValidate: progression is validated in handleRoleSubmit (toasts); native */}
          {/* required radios can block submit in some cases without firing onSubmit. */}
          <form onSubmit={onSubmit} id="role-form" noValidate>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Role Name *
                </label>
                <input
                  type="text"
                  name="roleName"
                  value={formData.roleName || ""}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Description
                </label>
                <textarea
                  name="roleDescription"
                  value={formData.roleDescription || ""}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300"
                  rows="3"
                />
              </div>

              {!isEditingSystemRole && (
              <div className="border-t border-gray-300 pt-4 mt-2">
                <h4 className="text-sm font-semibold mb-3">
                  Role Type {!editingRole ? "*" : ""}
                </h4>
                {editingRole && (
                  <p className="text-xs text-gray-600 mb-2">
                    Role type cannot be changed while editing an existing role.
                  </p>
                )}
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="wizardRoleType"
                      value="action_based"
                      checked={wizardRoleType === "action_based"}
                      onChange={(e) => setWizardRoleType(e.target.value)}
                      className="w-4 h-4"
                      disabled={!!editingRole}
                      required={!editingRole}
                    />
                    <span className="text-sm">Action Based Role</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="wizardRoleType"
                      value="access_based"
                      checked={wizardRoleType === "access_based"}
                      onChange={(e) => setWizardRoleType(e.target.value)}
                      className="w-4 h-4"
                      disabled={!!editingRole}
                    />
                    <span className="text-sm">Access Based Role</span>
                  </label>
                </div>
              </div>
              )}

              {!isEditingSystemRole && (
              <div className="border-t border-gray-300 pt-4 mt-2">
                <h4 className="text-sm font-semibold mb-3">
                  Assignment Management
                </h4>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="roleAssignmentType"
                      value="all"
                      checked={roleAssignmentType === "all"}
                      onChange={handleAssignAll}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">All Employees</span>
                  </label>
                  <div className="flex items-center space-x-2">
                    <label className="flex items-center space-x-2 cursor-pointer flex-1">
                      <input
                        type="radio"
                        name="roleAssignmentType"
                        value="filter"
                        checked={roleAssignmentType === "filter"}
                        onChange={handleAssignFilter}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Use Filter</span>
                    </label>
                    {roleAssignmentType === "filter" && (
                      <button
                        type="button"
                        onClick={() => setShowFilters(true)}
                        className="flex items-center space-x-1 rh-btn-primary text-sm px-3 py-1.5"
                        title="Open Filter"
                      >
                        <Filter className="w-4 h-4" />
                        <span>Go</span>
                      </button>
                    )}
                  </div>
                  {roleAssignmentType === "filter" && filterResults && (
                    <div className="ml-6 mt-2 p-2 bg-gray-50 border border-gray-200">
                      <p className="text-xs text-gray-600">
                        {filterResults.employeesData?.length || 0} employee(s)
                        selected
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowFilters(true)}
                        className="text-xs text-redhalo-navy hover:text-redhalo-red mt-1"
                      >
                        Change Filter
                      </button>
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
          </form>
        </div>

        <div className="flex justify-end space-x-2 p-4 border-t border-gray-300 flex-shrink-0">
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-500 text-white px-4 py-2 hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            className="rh-btn-primary rounded-md px-4 py-2 hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/40"
            onClick={() => {
              const synthetic = { preventDefault() {} };
              void onSubmit(synthetic);
            }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
